/**
 * Vista: Lista de Recepciones Visuales
 *
 * Estructura con pestañas:
 *   - Por OC: pedidos con progreso de recepción (flujo nuevo por OC)
 *   - Reingresos: recepciones de tractores existentes (placeholder, Fase 3)
 *   - Todas: lista plana histórica (flujo viejo, en transición)
 */

import recepcionService from '../services/recepcion.service.js';
import router from '../router/router.js';
import permisos from '../services/permissions.service.js';
import { subirVideoRecepcion } from '../utils/videoUploader.js';
import { fechaCorta } from '../utils/fechas.js';

export default {
  async render() {
    return `
      <div class="card">
        <div class="card-header" style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem;">
          <h2 class="card-title">📦 Recepciones Visuales</h2>
          ${permisos.canCreate('recepciones') ? `
            <button class="btn btn-primary" onclick="router.navigate('/almacen/recepciones/nueva')">
              ➕ Nueva Recepción
            </button>
          ` : ''}
        </div>

        <!-- Tabs -->
        <div class="tabs" style="display: flex; gap: 0.5rem; border-bottom: 2px solid var(--gray-200); margin-bottom: 1.5rem;">
          <!-- B5c (1-nov-2026): regresa la vista por pedido como «Por recibir»: los pedidos de
               Compras con unidades que todavía no llegan, cada uno con su pantalla de recepción.
               «Num. Serie» sigue siendo la lista de recepciones visuales por número de serie. -->
          <button class="tab-btn active" data-tab="por-oc" style="padding: 0.75rem 1.5rem; border: none; border-bottom: 3px solid var(--primary); background: none; cursor: pointer; font-weight: 600; color: var(--primary);">
            🚚 Por recibir
          </button>
          <button class="tab-btn" data-tab="todas" style="padding: 0.75rem 1.5rem; border: none; background: none; cursor: pointer; font-weight: 500; color: var(--gray-500);">
            📜 Num. Serie
          </button>
        </div>

        <!-- Contenido de tab -->
        <div id="tab-content"></div>
      </div>
    `;
  },

  async afterRender() {
    if (!permisos.canView('recepciones')) {
      if (permisos.primeraRuta) {
        router.navigate(permisos.primeraRuta);
      } else {
        document.getElementById('content').innerHTML = `
          <div style="display: flex; justify-content: center; align-items: center; height: 60vh;">
            <div style="text-align: center; color: var(--gray-500);">
              <h2>Acceso denegado</h2>
              <p>No tienes permisos para acceder a este módulo.</p>
            </div>
          </div>
        `;
      }
      return;
    }

    window.recepcionesListView = this;
    this.currentTab = 'por-oc';

    // Event listeners para tabs
    document.querySelectorAll('.tab-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const tab = e.currentTarget.dataset.tab;
        this.cambiarTab(tab);
      });
    });

    // Render tab por default (B5c: «Por recibir»)
    await this.renderTab('por-oc');
  },

  cambiarTab(tab) {
    if (this.currentTab === tab) return;

    this.currentTab = tab;
    document.querySelectorAll('.tab-btn').forEach(btn => {
      const activo = btn.dataset.tab === tab;
      btn.classList.toggle('active', activo);
      btn.style.borderBottom = activo ? '3px solid var(--primary)' : 'none';
      btn.style.color = activo ? 'var(--primary)' : 'var(--gray-500)';
      btn.style.fontWeight = activo ? '600' : '500';
    });

    this.renderTab(tab);
  },

  async renderTab(tab) {
    const container = document.getElementById('tab-content');

    if (tab === 'por-oc') {
      container.innerHTML = this._renderPorOCHtml();
      await this._initPorOC();
    } else if (tab === 'todas') {
      container.innerHTML = this._renderTodasHtml();
      await this._initTodas();
    }
  },

  // ============================================================
  // TAB: POR OC
  // ============================================================

  _renderPorOCHtml() {
    return `
      <div class="filters" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.5rem;">
        <div class="form-group">
          <label class="form-label">Buscar</label>
          <input type="text" id="poroc_busqueda" class="form-input" placeholder="Folio o proveedor" />
        </div>
        <div class="form-group">
          <label class="form-label">Mostrar</label>
          <select id="poroc_filtro" class="form-select">
            <option value="por_recibir">Con unidades por recibir</option>
            <option value="pendientes">Con algo pendiente (recibir o inspeccionar)</option>
            <option value="todas">Todos los pedidos</option>
          </select>
        </div>
      </div>

      <div class="table-container">
        <table class="table">
          <thead>
            <tr>
              <th></th>
              <th>Folio</th>
              <th class="tbl-hide-mobile">Proveedor</th>
              <th class="tbl-hide-mobile">Fecha</th>
              <th class="tbl-hide-mobile">Recibidos</th>
              <th class="tbl-hide-mobile">Por recibir</th>
              <th class="tbl-hide-mobile">Inspeccionados</th>
              <th class="tbl-hide-mobile">Estado</th>
              <th class="tbl-hide-mobile">Acciones</th>
            </tr>
          </thead>
          <tbody id="poroc_tbody">
            <tr><td colspan="9" style="text-align: center; padding: 2rem;">Cargando pedidos...</td></tr>
          </tbody>
        </table>
      </div>

      <div id="poroc_pagination" class="pagination"></div>
    `;
  },

  async _initPorOC() {
    this.porOC = {
      page: 1,
      limit: 10,
      filtro: 'por_recibir',
      busqueda: ''
    };

    document.getElementById('poroc_busqueda').addEventListener('input', (e) => {
      clearTimeout(this.porOC._timeout);
      this.porOC._timeout = setTimeout(() => {
        this.porOC.busqueda = e.target.value;
        this.porOC.page = 1;
        this._cargarPedidos();
      }, 400);
    });

    document.getElementById('poroc_filtro').addEventListener('change', (e) => {
      this.porOC.filtro = e.target.value;
      this.porOC.page = 1;
      this._cargarPedidos();
    });

    await this._cargarPedidos();
  },

  async _cargarPedidos() {
    try {
      const result = await recepcionService.listarPedidos({
        page: this.porOC.page,
        limit: this.porOC.limit,
        soloPendientes: this.porOC.filtro !== 'todas',
        soloPorRecibir: this.porOC.filtro === 'por_recibir',
        busqueda: this.porOC.busqueda
      });

      const pedidos = Array.isArray(result) ? result : (result.pedidos || result.data || []);
      const pagination = result.pagination || { page: 1, totalPages: 1 };

      this._renderPedidos(pedidos);
      this._renderPaginacionPorOC(pagination);
    } catch (error) {
      console.error('Error al cargar pedidos:', error);
      toast.error('Error al cargar pedidos');
      document.getElementById('poroc_tbody').innerHTML = `
        <tr><td colspan="9" style="text-align: center; padding: 2rem; color: var(--danger);">Error al cargar pedidos</td></tr>
      `;
    }
  },

  _renderPedidos(pedidos) {
    const tbody = document.getElementById('poroc_tbody');

    if (!pedidos || pedidos.length === 0) {
      tbody.innerHTML = `<tr><td colspan="9" style="text-align: center; padding: 2rem;">${this.porOC.filtro === 'por_recibir' ? 'No hay pedidos con unidades por recibir' : 'No hay pedidos que mostrar'}</td></tr>`;
      return;
    }

    tbody.innerHTML = pedidos.map(p => {
      const fecha = fechaCorta(p.fecha_pedido);
      const recibidos = p.recibidos || 0;
      const porLlegar = Number(p.por_llegar) || 0;
      const inspeccionados = p.inspeccionados || 0;
      const total = p.total_unidades || 0;
      const estado = p.estado_recepcion || 'pendiente';

      const badgeClass = {
        'completa': 'success',
        'en_recepcion': 'warning',
        'pendiente': 'secondary',
        'sin_unidades': 'info'
      }[estado] || 'secondary';

      const badgeTexto = {
        'completa': 'Completa',
        'en_recepcion': 'En recepción',
        'pendiente': 'Pendiente',
        'sin_unidades': 'Sin unidades'
      }[estado] || estado;

      const estadoBadge = `<span class="badge badge-${badgeClass}">${badgeTexto}</span>`;
      const accionBtn = `<button class="btn btn-sm btn-primary" onclick="window.recepcionesListView.abrirPedido(${p.id})">${porLlegar > 0 && permisos.canCreate('recepciones') ? 'Recibir' : 'Abrir'}</button>`;

      return `
        <tr>
          <td><button class="tbl-expand-btn" onclick="window.recepcionesListView.togglePorOCRow(${p.id})">&#9654;</button></td>
          <td><strong>${p.folio || '-'}</strong></td>
          <td class="tbl-hide-mobile">${p.proveedor_nombre || '-'}</td>
          <td class="tbl-hide-mobile">${fecha}</td>
          <td class="tbl-hide-mobile">${recibidos}/${total}</td>
          <td class="tbl-hide-mobile">${porLlegar > 0 ? `<strong>${porLlegar}</strong>` : '0'}</td>
          <td class="tbl-hide-mobile">${inspeccionados}/${total}</td>
          <td class="tbl-hide-mobile">${estadoBadge}</td>
          <td class="tbl-hide-mobile">${accionBtn}</td>
        </tr>
        <tr class="tbl-detail-row" id="detail-poroc-${p.id}">
          <td colspan="9" class="tbl-detail-cell">
            <dl class="tbl-detail-grid">
              <dt>Proveedor</dt><dd>${p.proveedor_nombre || '-'}</dd>
              <dt>Fecha</dt><dd>${fecha}</dd>
              <dt>Recibidos</dt><dd>${recibidos}/${total}</dd>
              <dt>Por recibir</dt><dd>${porLlegar}</dd>
              <dt>Inspeccionados</dt><dd>${inspeccionados}/${total}</dd>
              <dt>Estado</dt><dd>${estadoBadge}</dd>
            </dl>
            <div style="margin-top: var(--spacing-sm); display: flex; gap: var(--spacing-xs);">${accionBtn}</div>
          </td>
        </tr>
      `;
    }).join('');
  },

  togglePorOCRow(id) {
    const detailRow = document.getElementById(`detail-poroc-${id}`);
    const btn = detailRow?.previousElementSibling?.querySelector('.tbl-expand-btn');
    if (detailRow) {
      detailRow.classList.toggle('open');
      btn?.classList.toggle('open');
    }
  },

  _renderPaginacionPorOC(pagination) {
    const container = document.getElementById('poroc_pagination');
    const { page, totalPages } = pagination;

    if (!totalPages || totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    let html = '<div style="display: flex; gap: 0.5rem; justify-content: center; align-items: center; margin-top: 1rem;">';
    html += `<button class="btn btn-sm btn-secondary" onclick="window.recepcionesListView._cambiarPaginaPorOC(${page - 1})" ${page === 1 ? 'disabled' : ''}>← Anterior</button>`;

    for (let i = 1; i <= totalPages; i++) {
      if (i === page) {
        html += `<button class="btn btn-sm btn-primary" disabled>${i}</button>`;
      } else if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) {
        html += `<button class="btn btn-sm btn-secondary" onclick="window.recepcionesListView._cambiarPaginaPorOC(${i})">${i}</button>`;
      } else if (i === page - 3 || i === page + 3) {
        html += '<span>...</span>';
      }
    }

    html += `<button class="btn btn-sm btn-secondary" onclick="window.recepcionesListView._cambiarPaginaPorOC(${page + 1})" ${page === totalPages ? 'disabled' : ''}>Siguiente →</button>`;
    html += '</div>';

    container.innerHTML = html;
  },

  _cambiarPaginaPorOC(page) {
    this.porOC.page = page;
    this._cargarPedidos();
  },

  abrirPedido(id) {
    router.navigate(`/almacen/recepciones/pedido/${id}`);
  },

  // ============================================================
  // TAB: REINGRESOS (placeholder para Fase 3)
  // ============================================================

  // ============================================================
  // TAB: TODAS (lista plana histórica - flujo viejo)
  // ============================================================

  _renderTodasHtml() {
    return `
      <div class="filters" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.5rem;">
        <div class="form-group">
          <label class="form-label">Buscar</label>
          <input type="text" id="todas_busqueda" class="form-input" placeholder="Num. Serie o Modelo" />
        </div>
        <div class="form-group">
          <label class="form-label">Tipo</label>
          <select id="todas_tipoFilter" class="form-select">
            <option value="">Todos</option>
            <option value="compra">Compra</option>
            <option value="reingreso">Reingreso</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Estado General</label>
          <select id="todas_estadoFilter" class="form-select">
            <option value="">Todos</option>
            <option value="excelente">Excelente</option>
            <option value="bueno">Bueno</option>
            <option value="regular">Regular</option>
            <option value="malo">Malo</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Marca</label>
          <select id="todas_marcaFilter" class="form-select">
            <option value="">Todas</option>
            <option value="LOVOL">LOVOL</option>
            <option value="MAQMUT">MAQMUT</option>
            <option value="AOSHENG">AOSHENG</option>
            <option value="BEE MACH">BEE MACH</option>
          </select>
        </div>
      </div>

      <div class="table-container">
        <table class="table">
          <thead>
            <tr>
              <th></th>
              <th>Num. Serie</th>
              <th class="tbl-hide-mobile">Fecha</th>
              <th class="tbl-hide-mobile">Modelo</th>
              <th class="tbl-hide-mobile">Marca</th>
              <th class="tbl-hide-mobile">Estado</th>
              <th class="tbl-hide-mobile">Fotos</th>
              <th class="tbl-hide-mobile">Acciones</th>
            </tr>
          </thead>
          <tbody id="todas_tbody">
            <tr><td colspan="8" style="text-align: center; padding: 2rem;">Cargando recepciones...</td></tr>
          </tbody>
        </table>
      </div>

      <div id="todas_pagination" class="pagination"></div>
    `;
  },

  async _initTodas() {
    this.todas = {
      page: 1,
      limit: 10,
      filters: {}
    };

    document.getElementById('todas_busqueda').addEventListener('input', (e) => {
      clearTimeout(this.todas._timeout);
      this.todas._timeout = setTimeout(() => {
        this.todas.filters.busqueda = e.target.value;
        this.todas.page = 1;
        this._cargarTodas();
      }, 400);
    });

    document.getElementById('todas_tipoFilter').addEventListener('change', (e) => {
      this.todas.filters.tipo_recepcion = e.target.value;
      this.todas.page = 1;
      this._cargarTodas();
    });

    document.getElementById('todas_estadoFilter').addEventListener('change', (e) => {
      this.todas.filters.estado_general = e.target.value;
      this.todas.page = 1;
      this._cargarTodas();
    });

    document.getElementById('todas_marcaFilter').addEventListener('change', (e) => {
      this.todas.filters.marca = e.target.value;
      this.todas.page = 1;
      this._cargarTodas();
    });

    await this._cargarTodas();
  },

  async _cargarTodas() {
    try {
      const response = await recepcionService.listar({
        page: this.todas.page,
        limit: this.todas.limit,
        ...this.todas.filters
      });

      const recepciones = Array.isArray(response) ? response : (response.recepciones || response.data || response);
      const pagination = response.pagination || { page: 1, totalPages: 1 };

      this._renderTodas(recepciones);
      this._renderPaginacionTodas(pagination);
    } catch (error) {
      toast.error('Error al cargar recepciones');
      console.error(error);
    }
  },

  _renderTodas(recepciones) {
    const tbody = document.getElementById('todas_tbody');

    if (!recepciones || recepciones.length === 0) {
      tbody.innerHTML = `<tr><td colspan="7" style="text-align: center; padding: 2rem;">No se encontraron recepciones</td></tr>`;
      return;
    }

    tbody.innerHTML = recepciones.map(r => {
      const estadoBadge = `<span class="badge badge-${this._getBadgeClass(r.estado_general)}">${r.estado_general || '-'}</span>`;
      const fechaStr = fechaCorta(r.fecha_recepcion);
      const numSerieSafe = (r.num_serie || '').replace(/'/g, "\\'");
      // Subir video directo desde la lista (sin entrar al detalle), solo si la
      // recepción no está completada. Permiso dedicado `subir_video`.
      const accionVideo = (!r.completada && permisos.can('recepciones', 'subir_video'))
        ? `<button class="btn btn-sm btn-secondary" onclick="window.recepcionesListView.subirVideo(${r.id}, '${numSerieSafe}', ${!!r.video_url})">${r.video_url ? 'Reemplazar' : 'Subir'} video</button>`
        : '';
      const acciones = r.completada
        ? (permisos.canView('recepciones') ? `<button class="btn btn-sm btn-primary" onclick="window.recepcionesListView.verDetalle(${r.id})">Ver</button>` : '')
        : `${permisos.canEdit('recepciones') ? `<button class="btn btn-sm btn-primary" onclick="window.recepcionesListView.verDetalle(${r.id})">Editar</button>` : ''}
           ${permisos.canDelete('recepciones') ? `<button class="btn btn-sm btn-danger" onclick="window.recepcionesListView.eliminar(${r.id}, '${numSerieSafe}')">Eliminar</button>` : ''}
           ${accionVideo}`;

      return `
        <tr>
          <td><button class="tbl-expand-btn" onclick="window.recepcionesListView.toggleTodasRow(${r.id})">&#9654;</button></td>
          <td><strong>${r.num_serie}</strong></td>
          <td class="tbl-hide-mobile">${fechaStr}</td>
          <td class="tbl-hide-mobile">${r.modelo || '-'}</td>
          <td class="tbl-hide-mobile">${r.marca || '-'}</td>
          <td class="tbl-hide-mobile">${estadoBadge}</td>
          <td class="tbl-hide-mobile">${r.fotos?.length || 0} fotos</td>
          <td class="tbl-hide-mobile">
            <div style="display: flex; gap: 0.35rem;">${acciones}</div>
          </td>
        </tr>
        <tr class="tbl-detail-row" id="detail-todas-${r.id}">
          <td colspan="8" class="tbl-detail-cell">
            <dl class="tbl-detail-grid">
              <dt>Fecha</dt><dd>${fechaStr}</dd>
              <dt>Modelo</dt><dd>${r.modelo || '-'}</dd>
              <dt>Marca</dt><dd>${r.marca || '-'}</dd>
              <dt>Estado</dt><dd>${estadoBadge}</dd>
              <dt>Fotos</dt><dd>${r.fotos?.length || 0} fotos</dd>
            </dl>
            <div style="margin-top: var(--spacing-sm); display: flex; gap: var(--spacing-xs);">${acciones}</div>
          </td>
        </tr>
      `;
    }).join('');
  },

  async subirVideo(recepcionId, numSerie, existeVideo = false) {
    const ok = await subirVideoRecepcion(recepcionId, numSerie, {
      onProgress: (msg) => toast.info(msg),
      existeVideo
    });
    if (ok) await this._cargarTodas();
  },

  toggleTodasRow(id) {
    const detailRow = document.getElementById(`detail-todas-${id}`);
    const btn = detailRow?.previousElementSibling?.querySelector('.tbl-expand-btn');
    if (detailRow) {
      detailRow.classList.toggle('open');
      btn?.classList.toggle('open');
    }
  },

  _renderPaginacionTodas(pagination) {
    const container = document.getElementById('todas_pagination');
    const { page, totalPages } = pagination;

    if (!totalPages || totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    let html = '<div style="display: flex; gap: 0.5rem; justify-content: center; align-items: center; margin-top: 1rem;">';
    html += `<button class="btn btn-sm btn-secondary" onclick="window.recepcionesListView._cambiarPaginaTodas(${page - 1})" ${page === 1 ? 'disabled' : ''}>← Anterior</button>`;

    for (let i = 1; i <= totalPages; i++) {
      if (i === page) {
        html += `<button class="btn btn-sm btn-primary" disabled>${i}</button>`;
      } else if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) {
        html += `<button class="btn btn-sm btn-secondary" onclick="window.recepcionesListView._cambiarPaginaTodas(${i})">${i}</button>`;
      } else if (i === page - 3 || i === page + 3) {
        html += '<span>...</span>';
      }
    }

    html += `<button class="btn btn-sm btn-secondary" onclick="window.recepcionesListView._cambiarPaginaTodas(${page + 1})" ${page === totalPages ? 'disabled' : ''}>Siguiente →</button>`;
    html += '</div>';

    container.innerHTML = html;
  },

  _cambiarPaginaTodas(page) {
    this.todas.page = page;
    this._cargarTodas();
  },

  _getBadgeClass(estado) {
    return {
      'excelente': 'success',
      'bueno': 'info',
      'regular': 'warning',
      'malo': 'danger'
    }[estado] || 'secondary';
  },

  verDetalle(id) {
    router.navigate(`/almacen/recepciones/${id}`);
  },

  eliminar(id, numSerie) {
    modal.confirm(
      'Confirmar eliminación',
      `¿Está seguro de eliminar la recepción del tractor <strong>${numSerie}</strong>? Esta acción no se puede deshacer.`,
      async () => {
        try {
          await recepcionService.eliminar(id);
          toast.success('Recepción eliminada exitosamente');
          if (this.currentTab === 'todas') {
            this._cargarTodas();
          } else if (this.currentTab === 'por-oc') {
            this._cargarPedidos();
          }
        } catch (error) {
          toast.error('Error al eliminar recepción');
          console.error(error);
        }
      }
    );
  }
};
